import React, { useState, useEffect } from "react";
import { navigate } from "gatsby";
import queryString from "query-string";

// seo
import Seo from "../components/Seo";

// services
import { sendToLSQ } from "../services/LeadSquared";
import { Mixpanel, MixpanelEvents } from "../services/Mixpanel";

// constants
import constants from "../constants/LandingPage";

// utils
import { saveUTM, getUtmDataFromLS } from "../utils";
import { sendDiabetesConsultDataToSheets } from "../services/GoogleSheets";

// Images
import bannerImage from "../images/LandingPage/diabetes_conslt_banner.png";

// components
import LandingPageHeaderComponent from "../components/Header/LandingPageHeader";
import WebinarBannerComponent from "../components/LandingPages/Webinar/Banner/Banner";
import BookConsultationComponent from "../components/LandingPages/DiabetesConsultation/BookConsultation/BookConsultation";
import DiabetesReversalComponent from "../components/LandingPages/DiabetesConsultation/DiabetesReversal/DiabetesReversal";
import BodyMetabolismComponent from "../components/LandingPages/DiabetesConsultation/BodyMetabolism/BodyMetabolism";
import DoctorsSection from "../components/LandingPages/Webinar/Doctors/Doctors";
import WebinarTestimonialStories from "../components/LandingPages/Webinar/TestimonialCarousel/WebinarTestimonialStories";
import OurStoryComponent from "../components/LandingPages/DiabetesConsultation/OurStory/OurStory";
import ReverseManageComponent from "../components/LandingPages/Webinar/ReverseManage/ReverseManage";
import FaqComponent from "../components/LandingPages/DiabetesConsultation/FAQ/Faq";
import RegisterFormComponent from "../components/LandingPages/DiabetesConsultation/RegisterForm/RegisterForm";
import FooterComponent from "../components/LandingPages/DiabetesConsultation/Footer/Footer";

const TwinDiabetesLandingPage = props => {
  const [depth25, setDepth25] = useState(true);
  const [depth50, setDepth50] = useState(true);
  const [depth75, setDepth75] = useState(true);
  const [depth100, setDepth100] = useState(true);
  const [loading, setLoading] = useState(false);
  const [operatingSystem, setOperatingSystem] = useState(""); // To store operating system
  const [deviceType, setDeviceType] = useState(""); // To store the device type

  let utmParams = queryString.parse(props.location.search);

  // Save utm to local storage
  useEffect(() => {
    if (props.location.search) {
      saveUTM(props.location.search);
    }
  }, [props.location.search]);

  useEffect(() => {
    let loadTime =
      window.performance.timing.domContentLoadedEventEnd -
        window.performance.timing.navigationStart || 700;

    // Mixpanel Event
    Mixpanel.track(MixpanelEvents.page_viewed, {
      page_name: "twin-diabetes-consultation-landingPage",
      load_time: loadTime,
      campaign_id: utmParams.campaignid,
      device_type: deviceType,
    });
  }, [deviceType, utmParams.campaignid]);

  useEffect(() => {
    const handleScroll = () => {
      const pageYOffset = window.pageYOffset;
      const height = document.body.scrollHeight - window.innerHeight;
      const progress = Math.round((pageYOffset / height) * 100);

      if (progress === 25 && depth25) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: "twin-diabetes-consultation-landingPage",
          page_depth: progress,
        });
        setDepth25(false);
      } else if (progress === 50 && depth50) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: "twin-diabetes-consultation-landingPage",
          page_depth: progress,
        });
        setDepth50(false);
      } else if (progress === 75 && depth75) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: "twin-diabetes-consultation-landingPage",
          page_depth: progress,
        });
        setDepth75(false);
      } else if (progress === 100 && depth100) {
        Mixpanel.track(MixpanelEvents.page_depth_viewed, {
          page_name: "twin-diabetes-consultation-landingPage",
          page_depth: progress,
        });
        setDepth100(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [depth25, depth50, depth75, depth100]);

  // Find The Operating System Of The User
  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    let operatingSystem = "";

    if (userAgent.indexOf("Win") !== -1) {
      operatingSystem = "Windows OS";
    } else if (userAgent.indexOf("Mac") !== -1) {
      operatingSystem = "Mac OS";
    } else if (userAgent.indexOf("Linux") !== -1) {
      operatingSystem = "Linux OS";
    } else if (userAgent.indexOf("Android") !== -1) {
      operatingSystem = "Android OS";
    } else if (userAgent.indexOf("iOS") !== -1) {
      operatingSystem = "iOS";
    } else {
      operatingSystem = "Unknown";
    }

    setOperatingSystem(operatingSystem);
  }, []);

  // Find The Device Type Of The User
  useEffect(() => {
    // Function to update device type based on screen width
    const updateDeviceType = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setDeviceType("Mobile");
      } else if (width <= 1024) {
        setDeviceType("Tablet");
      } else {
        setDeviceType("Desktop");
      }
    };

    // Update device type when the component mounts
    updateDeviceType();

    // Add an event listener to update device type when the window is resized
    window.addEventListener("resize", updateDeviceType);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", updateDeviceType);
    };
  }, []);

  // To Track Mixpanel Event When User Leave Page
  useEffect(() => {
    const handlePageExit = event => {
      Mixpanel.track(MixpanelEvents.page_exit, {
        page_name: "twin-diabetes-consultation-landingPage",
      });
    };

    window.addEventListener("beforeunload", handlePageExit);

    return () => {
      window.removeEventListener("beforeunload", handlePageExit);
    };
  }, []);

  // Function --> Form Data send to LSQ
  const formDataToLsq = async values => {
    try {
      setLoading(true);

      // Get UTM data from localStorage
      const utmData = getUtmDataFromLS();

      // leadSquared params
      let leadSquaredParams = {
        FirstName: values.name,
        Phone: values.phone,
        EmailAddress: values.email,
        mx_Website_CTA: "twin-diabetes-consultation-landing-page",
        mx_Latest_Source: utmData.utm_source || "New Website",
        mx_Device_Mobile_Desktop: deviceType,
        mx_msc_Operating_System: operatingSystem,
      };

      // Add UTM parameters if they exist
      if (utmData.utm_source) {
        leadSquaredParams.mx_Latest_medium = utmData.utm_medium || "";
        leadSquaredParams.mx_Latest_campaign = utmData.utm_campaign || "";
        leadSquaredParams.mx_Latest_term = utmData.utm_term || "";
        leadSquaredParams.mx_Latest_content = utmData.utm_content || "";
      }

      await sendToLSQ(leadSquaredParams);

      // Send data to Google Sheets
      await sendDiabetesConsultDataToSheets(leadSquaredParams);

      setLoading(false);
      // redirection to thank you page
      navigate("/twin-slot-booking", {
        state: {
          name: values.name,
          email: values.email,
          phone: values.phone,
          heading: constants.CALENDLY_DETAILS.DIABETES_CONSULTATION_HEADING,
          calendlyUrl: constants.CALENDLY_DETAILS.DIABETES_CONSULT_URL,
          mixpanelPageName:
            constants.CALENDLY_MIXPANEL_PAGE_NAME.DIABETES_CONSULT_LP,
          campaignid: utmParams.campaignid,
        },
      });
    } catch (error) {
      console.log(
        "Error Leadsquared Form Diabetes Consultation LP - formDataToLsq:",
        error
      );
      setLoading(false);
      // redirection to thank you page
      navigate("/twin-slot-booking", {
        state: {
          name: values.name,
          email: values.email,
          phone: values.phone,
          calendlyUrl: constants.CALENDLY_DETAILS.DIABETES_CONSULT_URL,
          mixpanelPageName:
            constants.CALENDLY_MIXPANEL_PAGE_NAME.DIABETES_CONSULT_LP,
        },
      });
    }
  };

  const handleMixpanelCTA = (ctaText, ctaLocation) => {
    Mixpanel.track(MixpanelEvents.contact_button_clicked, {
      page_name: "twin-diabetes-consultation-landingPage",
      cta_text: ctaText,
      cta_location: ctaLocation,
    });
  };

  return (
    <div>
      <Seo
        title="Reverse Diabetes, Obesity & PMOS Naturally with Twin Health in India"
        description="Experience clinically proven, AI-driven program to reverse Diabetes, Hypertension, Obesity, PMOS & other chronic conditions with Twin Health now in India. 97% reduced blood sugar & medication, 98% symptom relief. Start your journey today."
      />

      <LandingPageHeaderComponent
        handleMixpanelCTA={handleMixpanelCTA}
        ctaText="Book a FREE consultation"
        ctaRedirectionUrl="/twin-diabetes-consultation-lp/#diabetesConsultLp__registerForm"
        mixpanelCtaLocation="Diabetes Consult Landing Page Header Section"
      />
      <WebinarBannerComponent
        webinarVideoLink={
          constants.LANDING_PAGE_VIDEOS.diabetesConsultBannerVideo
        }
        handleMixpanelCTA={handleMixpanelCTA}
        bannerImage={bannerImage}
        bannerHeading={
          constants.LANDING_PAGE_PARAS.diabetesConsultBannerHeading
        }
        bannerSubHeading={
          constants.LANDING_PAGE_PARAS.diabetesConsultBannerSubHeading
        }
        ctaText="Book a FREE consultation"
        ctaRedirectionUrl="/twin-diabetes-consultation-lp/#diabetesConsultLp__registerForm"
        mixpanelCtaLocation="Diabetes Consult Landing Page Top Banner Section"
        mixpanelPageName="twin-diabetes-consultation-landingPage"
        mixpanelVideoName="Diabetes Consultation Banner Landing Page"
      />
      <BookConsultationComponent />
      <DiabetesReversalComponent />
      <BodyMetabolismComponent
        heading={
          constants.BODY_METABOLISM_COMPONENT.diabetesConsultationHeading
        }
        subHeading={
          constants.BODY_METABOLISM_COMPONENT.diabetesConsultationSubHeading
        }
        cardDetails={constants.BODY_METABOLISM_DIABETES_CONSULT_CARD}
        handleMixpanelCTA={handleMixpanelCTA}
        ctaText="Book a FREE consultation"
        ctaRedirectionUrl="/twin-diabetes-consultation-lp/#diabetesConsultLp__registerForm"
        mixpanelCtaLocation="Diabetes Consult Landing Page Body Metabolism Section"
      />
      <WebinarTestimonialStories
        handleMixpanelCTA={handleMixpanelCTA}
        testimonialDetails={constants.WEBINAR_TESTIMONIAL_DETAILS}
        ctaText="Book a FREE consultation"
        ctaRedirectionUrl="/twin-diabetes-consultation-lp/#diabetesConsultLp__registerForm"
        mixpanelCtaLocation="Diabetes Consult Landing Page Testimonial Section"
      />
      <OurStoryComponent
        componentHeading={
          constants.LANDING_PAGE_PARAS.diabetesConsultOurStoryHeading
        }
        footerText={constants.LANDING_PAGE_PARAS.diabetesConsultOurStoryFooter}
        diabetesConsultOurStory={constants.DIABETES_CONSULT_OUR_STORY}
      />
      <DoctorsSection
        doctorDetails={constants.MEDICAL_ADVISORY_DOCTORS}
        handleMixpanelCTA={handleMixpanelCTA}
        doctorHeading={
          constants.LANDING_PAGE_PARAS.diabetesConsultDoctorHeading
        }
        ctaText="Book a FREE consultation"
        ctaRedirectionUrl="/twin-diabetes-consultation-lp/#diabetesConsultLp__registerForm"
        mixpanelCtaLocation="Diabetes Consult Landing Page Medical Advisory Doctor Section"
      />
      <ReverseManageComponent
        heading={constants.REVERSE_OTHERS_MANAGE_CONTENT.diabetesHeading}
        subHeading={constants.REVERSE_OTHERS_MANAGE_CONTENT.diabetesSubHeading}
        tableHeader={constants.WEBINAR_REVERSEMANAGE_HEADER}
        tableData={constants.WEBINAR_REVERSEMANAGE}
      />
      <FaqComponent
        faqDetails={constants.DIABETES_CONSULT_FAQ}
        handleMixpanelCTA={handleMixpanelCTA}
      />
      <div id="diabetesConsultLp__registerForm">
        <RegisterFormComponent
          loading={loading}
          formDataToLsq={formDataToLsq}
          formHeading="Get your one on one consultation"
          ctaText="Book a FREE consultation"
          mixpanelPageName="twin-diabetes-consultation-landingPage"
        />
      </div>
      <FooterComponent termsRedirectionUrl="/terms-lp/" />
    </div>
  );
};

export default TwinDiabetesLandingPage;
